/**
 * 水平線角タイルの取得・参照（§5）。初回ダウンロード→IndexedDBへ保存。
 * タイル未取得・範囲外は開空（遮蔽なし）フォールバック＝安全側。
 *
 * エリアは一度に1つだけ扱う（経路探索が単一グリッド前提）。どのエリアを使うかは
 * `shared/areas.ts` の目録から選ぶ。
 */
import { areaById, TerrainTile } from "@uvk/shared";
import type { HeightMode, LatLon, TerrainArea, TerrainCell } from "@uvk/shared";
import { db } from "./db.js";
import { fetchTerrainTile } from "./api.js";

/** `{areaId}:{mode}` → タイル（null は取得失敗） */
const loaded = new Map<string, TerrainTile | null>();

function key(areaId: string, mode: HeightMode): string {
  return `${areaId}:${mode}`;
}

function tileName(areaId: string, mode: HeightMode): string {
  return `${areaId}_${mode}.uvkt`;
}

function safeDecode(buf: ArrayBuffer | null): TerrainTile | null {
  if (!buf) return null;
  try {
    return TerrainTile.decode(buf);
  } catch (e) {
    console.warn("terrain tile decode failed:", e);
    return null;
  }
}

/** タイルヘッダが目録と一致するか（原点・セルの大きさ・セル数） */
function matchesArea(tile: TerrainTile, area: TerrainArea): boolean {
  const near = (a: number, b: number, tol: number) => Math.abs(a - b) <= tol;
  return (
    near(tile.originLat, area.originLat, 1e-4) &&
    near(tile.originLon, area.originLon, 1e-4) &&
    near(tile.cellSizeM, area.cellM, 1e-6) &&
    tile.cellsX === area.cellsX &&
    tile.cellsY === area.cellsY
  );
}

function describe(t: { originLat: number; originLon: number }, cell: number, nx: number, ny: number) {
  return `(${t.originLat},${t.originLon},${cell}m,${nx}x${ny})`;
}

export async function ensureTerrain(mode: HeightMode, areaId: string): Promise<void> {
  const k = key(areaId, mode);
  if (loaded.has(k)) return;
  const area = areaById(areaId);
  const name = tileName(areaId, mode);

  let buf = (await db.get<ArrayBuffer>("terrain", name)) ?? null;
  let tile = safeDecode(buf);
  // 端末に残っているタイルを取り直す条件：
  //   ・旧v1タイル（歩行可否ビットが無く経路探索に使えない）
  //   ・目録と範囲が食い違う（＝タイルを作り直した）
  // タイルはファイル名が同じまま中身だけ差し替わるため、後者の判定が無いと
  // 既存ユーザーだけ旧範囲のタイルを使い続ける。エリアを広げても
  // 「範囲外」が直らない、という形で出る（実際に起きた）。
  if (!tile || tile.version < 2 || !matchesArea(tile, area)) {
    buf = await fetchTerrainTile(name);
    const fresh = safeDecode(buf);
    if (fresh) {
      tile = fresh;
      await db.put("terrain", buf, name);
    }
  }
  // 取り直してもなお食い違うなら、目録とタイル生成の PRESETS のどちらかが
  // 直し忘れられている（両方を直す運用なので、気づけるよう残す）
  if (tile && !matchesArea(tile, area)) {
    console.warn(
      `terrain: areas.ts と ${name} が不一致。` +
        `目録=${describe(area, area.cellM, area.cellsX, area.cellsY)} ` +
        `タイル=${describe(tile, tile.cellSizeM, tile.cellsX, tile.cellsY)}`,
    );
  }
  loaded.set(k, tile);
}

/** 現在地の地形セル。未取得はnull（呼び出し側＝エンジンが開空扱い） */
export function terrainCellAt(pos: LatLon, mode: HeightMode, areaId: string): TerrainCell | null {
  return loaded.get(key(areaId, mode))?.lookup(pos) ?? null;
}

export function terrainAvailable(mode: HeightMode, areaId: string): boolean {
  return !!loaded.get(key(areaId, mode));
}

/** 経路探索用：タイル本体（未取得はnull） */
export function terrainTile(mode: HeightMode, areaId: string): TerrainTile | null {
  return loaded.get(key(areaId, mode)) ?? null;
}
